import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

function ContactUs() {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState({ loading: false, success: false, error: '' });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: '' });

        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/submit_contact.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to send message.');
            }

            setStatus({ loading: false, success: true, error: '' });
            setFormData({ name: '', email: '', message: '' }); // Reset form
        } catch (error) {
            setStatus({ loading: false, success: false, error: error.message });
        }
    };
    return (
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg max-w-4xl mx-auto my-10">
            <Helmet>
                <title>Contact Us | Health Portal</title>
            </Helmet>
            <h1 className="text-3xl font-bold text-brand-secondary mb-6">Contact Us</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                    <h2 className="text-xl font-semibold text-brand-primary mb-4">Get in Touch</h2>
                    <p className="text-gray-700 mb-6">
                        Have questions about your Health Card, need help with your subscription, or want to report an issue? Our team is here to help!
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <svg className="w-6 h-6 text-brand-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                            <div>
                                <h3 className="font-semibold text-gray-800">Email Address</h3>
                                <p className="text-gray-600">searchisend@smartcityajmer.com</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <svg className="w-6 h-6 text-brand-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                            <div>
                                <h3 className="font-semibold text-gray-800">WhatsApp</h3>
                                <p className="text-gray-600">+91-9784380000</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <svg className="w-6 h-6 text-brand-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                            <div>
                                <h3 className="font-semibold text-gray-800">Legal Business Name & Address</h3>
                                <p className="text-gray-600">
                                    Smart City Ajmer operating as Search is End!<br />
                                    Ajmer, Rajasthan<br />
                                    India
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <svg className="w-6 h-6 text-brand-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path></svg>
                            <div>
                                <h3 className="font-semibold text-gray-800">Social Media</h3>
                                <div className="flex gap-4 mt-2">
                                    <a href="https://www.facebook.com/searchisend01" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-emerald-700">Facebook</a>
                                    <a href="https://www.instagram.com/searchisend/" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-emerald-700">Instagram</a>
                                    <a href="https://www.youtube.com/@searchisend" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-emerald-700">YouTube</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Send us a Message</h2>
                    {status.success ? (
                        <div className="bg-emerald-50 text-emerald-700 p-4 rounded-xl border border-emerald-200 flex flex-col items-center justify-center text-center h-full min-h-[250px]">
                            <svg className="w-12 h-12 mb-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            <h3 className="font-bold text-lg">Message Sent!</h3>
                            <p className="text-sm mt-1">Thank you for reaching out. We will get back to you shortly.</p>
                            <button onClick={() => setStatus({ ...status, success: false })} className="mt-4 text-emerald-600 font-semibold hover:underline text-sm">Send another message</button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Your Name" className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Your Email" className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
                            <textarea name="message" value={formData.message} onChange={handleChange} required placeholder="How can we help?" rows="4" className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary"></textarea>

                            {status.error && <div className="text-red-500 text-sm font-medium">{status.error}</div>}

                            <button type="submit" disabled={status.loading} className="w-full bg-brand-primary hover:bg-emerald-700 text-white font-bold py-3 rounded-md transition-colors disabled:bg-slate-400">
                                {status.loading ? 'Sending...' : 'Submit'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ContactUs;
